import { difference } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { ExpiryStatus } from '../../../../../Generated/Commodity-Management-Api';
import { GroupingOption } from '../../../../../Types';
import { StoneXDivider, StoneXRow } from '../../../../StoneX';
import { StoneXAutocomplete, StoneXButton, StoneXCheckbox, StoneXSelect, StoneXTextField } from '../../../../StoneXMui';
import { StoneXTransferList } from '../../../../StoneXMui/StoneXTransferList/StoneXTransferList';
import { FixedCallSeriesOption, SeriesOption, VarianceColumns, VarianceReportColumn, VarianceReportConfiguration, VolumeSeriesOption } from '../../Reports/Reports/Variance/Types';
import { BaseReportSettingsPageProps } from './types';
import { MonthInteger } from '../../../../../Types/MonthDictionary';

const DEFAULT_VARIANCE_COLUMNS = Object.keys(VarianceColumns) as VarianceReportColumn[];
const VARIANCE_SERIES_OPTIONS: SeriesOption[] = ['budgetValue', 'marketValue', 'blendedValue', 'budgetPrice', 'marketPrice', 'blendedPrice'];
const FIXED_CALL_SERIES_OPTIONS: FixedCallSeriesOption[] = ['underlyingCallHedgedVolume', 'underlyingFixedHedgeVolume', 'strikePrice', 'budgetPrice', 'marketPrice', 'blendedPrice'];
const VOLUME_SERIES_OPTIONS: VolumeSeriesOption[] = ['budgetVolume', 'allInHedgedVolume', 'underlyingHedgedVolume', 'basisHedgedVolume'];

function buildSeriesSettings<TOption extends string>(
  options: readonly TOption[],
  existing?: Partial<Record<TOption, { color?: string; opacity?: number }>>,
): Record<TOption, { color?: string; opacity?: number }> {
  return options.reduce((result, option) => {
    result[option] = {
      color: existing?.[option]?.color,
      opacity: existing?.[option]?.opacity,
    };
    return result;
  }, {} as Record<TOption, { color?: string; opacity?: number }>);
}

function buildVolumeSeriesSettings(
  existing?: Partial<Record<VolumeSeriesOption, { enabled?: boolean; color?: string; opacity?: number }>>,
): Record<VolumeSeriesOption, { enabled?: boolean; color?: string; opacity?: number }> {
  return VOLUME_SERIES_OPTIONS.reduce((result, option) => {
    result[option] = {
      enabled: existing?.[option]?.enabled ?? true,
      color: existing?.[option]?.color,
      opacity: existing?.[option]?.opacity,
    };
    return result;
  }, {} as Record<VolumeSeriesOption, { enabled?: boolean; color?: string; opacity?: number }>);
}

function getSeriesDisplayLabel(seriesOption: string): string {
  return seriesOption
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (value) => value.toUpperCase())
    .trim();
}

function parseVarianceReportConfiguration(configurationJson: string): VarianceReportConfiguration {
  return JSON.parse(configurationJson) as VarianceReportConfiguration;
}

function getInitialAvailableColumns(configuration: VarianceReportConfiguration): VarianceReportColumn[] {
  return configuration.columns?.available?.length ? configuration.columns.available : DEFAULT_VARIANCE_COLUMNS;
}

function getInitialActiveColumns(configuration: VarianceReportConfiguration, availableColumns: VarianceReportColumn[]): VarianceReportColumn[] {
  const configuredOrDefault = configuration.columns?.active?.length ? configuration.columns.active : availableColumns;
  return configuredOrDefault.filter((column) => availableColumns.includes(column));
}

export default function VarianceToBudgetReportSettingsPage({ report, isSaving, onDeleteReport, onUpdateReport }: BaseReportSettingsPageProps) {
  const groupingOptions = useMemo<GroupingOption[]>(() => ['Commodity', 'BudgetMonth', 'BudgetingTier', 'Tiers', 'Counterparty'], []);
  const monthOptions = useMemo<number[]>(() => Array.from({ length: 12 }, (_, idx) => idx + 1), []);

  const initialConfiguration = useMemo(() => parseVarianceReportConfiguration(report.configuration), [report.configuration]);
  const initialAvailableColumns = useMemo(() => getInitialAvailableColumns(initialConfiguration), [initialConfiguration]);
  const initialActiveColumns = useMemo(() => getInitialActiveColumns(initialConfiguration, initialAvailableColumns), [initialConfiguration, initialAvailableColumns]);

  const [reportName, setReportName] = useState<string>(report.reportName);
  const [configuration, setConfiguration] = useState<VarianceReportConfiguration>(initialConfiguration);

  const [availableColumns, setAvailableColumns] = useState<VarianceReportColumn[]>(initialAvailableColumns);
  const [unavailableColumns, setUnavailableColumns] = useState<VarianceReportColumn[]>(difference(DEFAULT_VARIANCE_COLUMNS, initialAvailableColumns));
  const [inactiveColumns, setInactiveColumns] = useState<VarianceReportColumn[]>(difference(initialAvailableColumns, initialActiveColumns));
  const [activeColumns, setActiveColumns] = useState<VarianceReportColumn[]>(initialActiveColumns);

  const [varianceChartEnabled, setVarianceChartEnabled] = useState<boolean>(initialConfiguration.chart?.varianceChart?.enabled ?? false);
  const [fixedCallCoverageChartEnabled, setFixedCallCoverageChartEnabled] = useState<boolean>(initialConfiguration.chart?.fixedCallCoverageChart?.enabled ?? false);
  const [volumeChartEnabled, setVolumeChartEnabled] = useState<boolean>(initialConfiguration.chart?.volumeChart?.enabled ?? false);
  const [varianceSeriesSettings, setVarianceSeriesSettings] = useState<Record<SeriesOption, { color?: string; opacity?: number }>>(
    buildSeriesSettings(VARIANCE_SERIES_OPTIONS, initialConfiguration.chart?.varianceValueChart?.series),
  );
  const [fixedCallSeriesSettings, setFixedCallSeriesSettings] = useState<Record<FixedCallSeriesOption, { color?: string; opacity?: number }>>(
    buildSeriesSettings(FIXED_CALL_SERIES_OPTIONS, initialConfiguration.chart?.fixedValueChart?.series),
  );
  const [volumeSeriesSettings, setVolumeSeriesSettings] = useState<Record<VolumeSeriesOption, { enabled?: boolean; color?: string; opacity?: number }>>(
    buildVolumeSeriesSettings(initialConfiguration.chart?.volumeValueChart?.series),
  );
  const [expiryStatus, setExpiryStatus] = useState<ExpiryStatus>(initialConfiguration.behavior?.expiryStatus ?? ExpiryStatus.Open);
  const [hideParentHeaders, setHideParentHeaders] = useState<boolean>(initialConfiguration.behavior?.hideParentHeaders ?? false);
  const [level1GroupingOption, setLevel1GroupingOption] = useState<GroupingOption | null>(initialConfiguration.behavior?.grouping?.[0] ?? 'Commodity');
  const [level2GroupingOption, setLevel2GroupingOption] = useState<GroupingOption | null>(initialConfiguration.behavior?.grouping?.[1] ?? null);

  const [showBudgetStartDate, setShowBudgetStartDate] = useState<boolean>(!(initialConfiguration.inputs?.budgetStartDate?.hide ?? false));
  const [startDateStrategy, setStartDateStrategy] = useState<'fiscalYearStart' | 'currentMonth'>(initialConfiguration.inputs?.budgetStartDate?.strategy ?? 'fiscalYearStart');
  const [startDateOffset, setStartDateOffset] = useState<number>(initialConfiguration.inputs?.budgetStartDate?.offset ?? 0);
  const [rollOffMonth, setRollOffMonth] = useState<number | null>(initialConfiguration.inputs?.budgetStartDate?.rollOffInToNewYearMonth ?? null);

  const [showBudgetEndDate, setShowBudgetEndDate] = useState<boolean>(!(initialConfiguration.inputs?.budgetEndDate?.hide ?? false));
  const [endDateOffsetFromStart, setEndDateOffsetFromStart] = useState<number | undefined>(initialConfiguration.inputs?.budgetEndDate?.offsetFromStartMonth);

  const columnOrderMap = useMemo<{ [key: string]: number }>(() => {
    const keys = Object.keys(VarianceColumns);
    const map: { [key: string]: number } = {};
    keys.forEach((value: string, index) => (map[value] = index));
    return map;
  }, []);

  useEffect(() => {
    const parsed = parseVarianceReportConfiguration(report.configuration);
    const currentAvailableColumns = getInitialAvailableColumns(parsed);
    const currentUnavailableColumns = difference(DEFAULT_VARIANCE_COLUMNS, currentAvailableColumns);
    const currentActiveColumns = getInitialActiveColumns(parsed, currentAvailableColumns);
    const currentInactiveColumns = difference(currentAvailableColumns, currentActiveColumns);

    setReportName(report.reportName);
    setConfiguration(parsed);

    setAvailableColumns(currentAvailableColumns);
    setUnavailableColumns(currentUnavailableColumns);
    setInactiveColumns(currentInactiveColumns);
    setActiveColumns(currentActiveColumns);

    applyChartFlags(parsed);
    setVarianceSeriesSettings(buildSeriesSettings(VARIANCE_SERIES_OPTIONS, parsed.chart?.varianceValueChart?.series));
    setFixedCallSeriesSettings(buildSeriesSettings(FIXED_CALL_SERIES_OPTIONS, parsed.chart?.fixedValueChart?.series));
    setVolumeSeriesSettings(buildVolumeSeriesSettings(parsed.chart?.volumeValueChart?.series));
    setExpiryStatus(parsed.behavior?.expiryStatus ?? ExpiryStatus.Open);
    setHideParentHeaders(parsed.behavior?.hideParentHeaders ?? false);
    setLevel1GroupingOption(parsed.behavior?.grouping?.[0] ?? 'Commodity');
    setLevel2GroupingOption(parsed.behavior?.grouping?.[1] ?? null);

    setShowBudgetStartDate(!(parsed.inputs?.budgetStartDate?.hide ?? false));
    setStartDateStrategy(parsed.inputs?.budgetStartDate?.strategy ?? 'fiscalYearStart');
    setStartDateOffset(parsed.inputs?.budgetStartDate?.offset ?? 0);
    setRollOffMonth(parsed.inputs?.budgetStartDate?.rollOffInToNewYearMonth ?? null);

    setShowBudgetEndDate(!(parsed.inputs?.budgetEndDate?.hide ?? false));
    setEndDateOffsetFromStart(parsed.inputs?.budgetEndDate?.offsetFromStartMonth);
  }, [report]);

  function applyChartFlags(parsed: VarianceReportConfiguration) {
    setVarianceChartEnabled(parsed.chart?.varianceChart?.enabled ?? false);
    setFixedCallCoverageChartEnabled(parsed.chart?.fixedCallCoverageChart?.enabled ?? false);
    setVolumeChartEnabled(parsed.chart?.volumeChart?.enabled ?? false);
  }

  function onAvailableColumnsUpdate(updatedUnavailableColumns: VarianceReportColumn[], updatedAvailableColumns: VarianceReportColumn[]) {
    const filteredActiveColumns = activeColumns.filter((column) => updatedAvailableColumns.includes(column));
    const updatedInactiveColumns = difference(updatedAvailableColumns, filteredActiveColumns);

    setUnavailableColumns(updatedUnavailableColumns);
    setAvailableColumns(updatedAvailableColumns);
    setActiveColumns(filteredActiveColumns);
    setInactiveColumns(updatedInactiveColumns);
  }

  function onActiveColumnsUpdate(updatedInactiveColumns: VarianceReportColumn[], updatedActiveColumns: VarianceReportColumn[]) {
    setInactiveColumns(updatedInactiveColumns);
    setActiveColumns(updatedActiveColumns);
  }

  function updateVarianceSeriesColor(seriesOption: SeriesOption, color: string) {
    setVarianceSeriesSettings((previous) => ({
      ...previous,
      [seriesOption]: {
        ...previous[seriesOption],
        color: color.trim() || undefined,
      },
    }));
  }

  function updateVarianceSeriesOpacity(seriesOption: SeriesOption, opacity: string) {
    setVarianceSeriesSettings((previous) => ({
      ...previous,
      [seriesOption]: {
        ...previous[seriesOption],
        opacity: opacity.trim() === '' ? undefined : Number(opacity),
      },
    }));
  }

  function updateFixedCallSeriesColor(seriesOption: FixedCallSeriesOption, color: string) {
    setFixedCallSeriesSettings((previous) => ({
      ...previous,
      [seriesOption]: {
        ...previous[seriesOption],
        color: color.trim() || undefined,
      },
    }));
  }

  function updateFixedCallSeriesOpacity(seriesOption: FixedCallSeriesOption, opacity: string) {
    setFixedCallSeriesSettings((previous) => ({
      ...previous,
      [seriesOption]: {
        ...previous[seriesOption],
        opacity: opacity.trim() === '' ? undefined : Number(opacity),
      },
    }));
  }

  function updateVolumeSeriesEnabled(seriesOption: VolumeSeriesOption, enabled: boolean) {
    setVolumeSeriesSettings((previous) => ({
      ...previous,
      [seriesOption]: {
        ...previous[seriesOption],
        enabled,
      },
    }));
  }

  function updateVolumeSeriesColor(seriesOption: VolumeSeriesOption, color: string) {
    setVolumeSeriesSettings((previous) => ({
      ...previous,
      [seriesOption]: {
        ...previous[seriesOption],
        color: color.trim() || undefined,
      },
    }));
  }

  function updateVolumeSeriesOpacity(seriesOption: VolumeSeriesOption, opacity: string) {
    setVolumeSeriesSettings((previous) => ({
      ...previous,
      [seriesOption]: {
        ...previous[seriesOption],
        opacity: opacity.trim() === '' ? undefined : Number(opacity),
      },
    }));
  }

  function resetConfiguration() {
    const parsed = parseVarianceReportConfiguration(report.configuration);
    const currentAvailableColumns = getInitialAvailableColumns(parsed);
    const currentUnavailableColumns = difference(DEFAULT_VARIANCE_COLUMNS, currentAvailableColumns);
    const currentActiveColumns = getInitialActiveColumns(parsed, currentAvailableColumns);
    const currentInactiveColumns = difference(currentAvailableColumns, currentActiveColumns);

    setReportName(report.reportName);
    setConfiguration(parsed);

    setAvailableColumns(currentAvailableColumns);
    setUnavailableColumns(currentUnavailableColumns);
    setInactiveColumns(currentInactiveColumns);
    setActiveColumns(currentActiveColumns);

    applyChartFlags(parsed);
    setVarianceSeriesSettings(buildSeriesSettings(VARIANCE_SERIES_OPTIONS, parsed.chart?.varianceValueChart?.series));
    setFixedCallSeriesSettings(buildSeriesSettings(FIXED_CALL_SERIES_OPTIONS, parsed.chart?.fixedValueChart?.series));
    setVolumeSeriesSettings(buildVolumeSeriesSettings(parsed.chart?.volumeValueChart?.series));
    setExpiryStatus(parsed.behavior?.expiryStatus ?? ExpiryStatus.Open);
    setHideParentHeaders(parsed.behavior?.hideParentHeaders ?? false);
    setLevel1GroupingOption(parsed.behavior?.grouping?.[0] ?? 'Commodity');
    setLevel2GroupingOption(parsed.behavior?.grouping?.[1] ?? null);

    setShowBudgetStartDate(!(parsed.inputs?.budgetStartDate?.hide ?? false));
    setStartDateStrategy(parsed.inputs?.budgetStartDate?.strategy ?? 'fiscalYearStart');
    setStartDateOffset(parsed.inputs?.budgetStartDate?.offset ?? 0);
    setRollOffMonth(parsed.inputs?.budgetStartDate?.rollOffInToNewYearMonth ?? null);

    setShowBudgetEndDate(!(parsed.inputs?.budgetEndDate?.hide ?? false));
    setEndDateOffsetFromStart(parsed.inputs?.budgetEndDate?.offsetFromStartMonth);
  }

  function saveConfiguration() {
    const grouping = [level1GroupingOption, level2GroupingOption].filter((x) => x !== null) as GroupingOption[];

    const nextConfiguration: VarianceReportConfiguration = {
      ...configuration,
      inputs: {
        budgetStartDate: {
          hide: !showBudgetStartDate,
          strategy: startDateStrategy,
          offset: startDateOffset,
          rollOffInToNewYearMonth: rollOffMonth as MonthInteger ?? undefined,
        },
        budgetEndDate: {
          hide: !showBudgetEndDate,
          offsetFromStartMonth: endDateOffsetFromStart,
        },
      },
      behavior: {
        ...configuration.behavior,
        expiryStatus,
        grouping,
        hideParentHeaders,
      },
      columns: {
        available: availableColumns,
        active: activeColumns,
      },
      chart: {
        ...configuration.chart,
        varianceChart: { enabled: varianceChartEnabled },
        fixedCallCoverageChart: { enabled: fixedCallCoverageChartEnabled },
        volumeChart: { enabled: volumeChartEnabled },
        varianceValueChart: {
          series: varianceSeriesSettings,
        },
        fixedValueChart: {
          series: fixedCallSeriesSettings,
        },
        volumeValueChart: {
          series: volumeSeriesSettings,
        },
      },
    };

    onUpdateReport({
      ...report,
      reportName: reportName.trim(),
      configuration: JSON.stringify(nextConfiguration),
    });

    setConfiguration(nextConfiguration);
  }

  return (
    <div>
      <StoneXRow>
        <StoneXTextField value={reportName} label="Report Name" onUpdate={setReportName} />
      </StoneXRow>

      <StoneXDivider />

      <StoneXRow align='start'>
        <div style={{ flex: '1 1' }}>

          <StoneXRow>
            <h3>Inputs</h3>
          </StoneXRow>

          <StoneXRow>
            <StoneXCheckbox
              label="Show Budget Start Date"
              helperText="Shows or hides the date selector"
              checked={showBudgetStartDate}
              onUpdate={setShowBudgetStartDate}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXSelect
              label="Start Strategy"
              helperText="The starting month for the report"
              options={['fiscalYearStart', 'currentMonth']}
              getId={(x) => x}
              getOptionLabel={(x) => x === 'fiscalYearStart' ? 'Start of the year' : 'Current month'}
              value={startDateStrategy}
              onChange={(value) => setStartDateStrategy((value ?? 'fiscalYearStart') as 'fiscalYearStart' | 'currentMonth')}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXTextField
              label="Start Offset"
              helperText="The number of months from current month to start from"
              type="number"
              value={startDateOffset}
              onUpdate={(value) => setStartDateOffset(Number(value ?? 0))}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXSelect
              label="Roll-off Month"
              helperText="How many months before end of year to roll off to next year"
              options={monthOptions}
              getId={(x) => x}
              getOptionLabel={(x) => x}
              value={rollOffMonth}
              clearable
              onChange={(value) => setRollOffMonth(value ?? null)}
            />
          </StoneXRow>

          <StoneXRow>
            <StoneXCheckbox
              label="Show Budget End Date"
              helperText="Shows or hides the date selector"
              checked={showBudgetEndDate}
              onUpdate={setShowBudgetEndDate}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXTextField
              label="End Offset From Start"
              helperText="The number of months to run the report for"
              type="number"
              placeholder="12"
              value={endDateOffsetFromStart}
              onUpdate={(value) => setEndDateOffsetFromStart(Number(value))}
            />
          </StoneXRow>
        </div>

        <div style={{ flex: '1 1' }}>
          
          <StoneXRow>
            <h3>Behavior</h3>
          </StoneXRow>

          <StoneXRow>
            <StoneXAutocomplete
              label="Group By"
              helperText="The first grouping level"
              options={groupingOptions}
              value={level1GroupingOption}
              onUpdate={setLevel1GroupingOption}
              getOptionLabel={(x) => x}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXAutocomplete
              label="Then By (Optional)"
              helperText="The second grouping level"
              options={groupingOptions}
              value={level2GroupingOption}
              onUpdate={setLevel2GroupingOption}
              getOptionLabel={(x) => x}
            />
          </StoneXRow>
          <StoneXRow>
          <StoneXSelect
              label="Months to show"
              helperText="Whether to include expired months, open months, or all months"
              options={[ExpiryStatus.All, ExpiryStatus.Expired, ExpiryStatus.Open]}
              getId={(x) => x}
              getOptionLabel={(x) => x}
              value={expiryStatus}
              onChange={(value) => setExpiryStatus(value ?? ExpiryStatus.Open)}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXCheckbox
              label="Hide Parent Headers"
              helperText="Render columns without their grouped parent header row"
              checked={hideParentHeaders}
              onUpdate={setHideParentHeaders}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXCheckbox
              label="Variance Chart"
              helperText="Show the variance value/price chart"
              checked={varianceChartEnabled}
              onUpdate={setVarianceChartEnabled}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXCheckbox
              label="Fixed/Call Coverage Chart"
              helperText="Show the fixed and call hedge coverage chart"
              checked={fixedCallCoverageChartEnabled}
              onUpdate={setFixedCallCoverageChartEnabled}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXCheckbox
              label="Volume Chart"
              helperText="Show the volume chart"
              checked={volumeChartEnabled}
              onUpdate={setVolumeChartEnabled}
            />
          </StoneXRow>
        </div>
      </StoneXRow>

      <StoneXDivider />

      <h3>Chart Configuration</h3>
      <p>Enter colors as a hex and opacity as a decimal. Leave blank to use defaults</p>

      {varianceChartEnabled && (
        <StoneXRow>
          <div>
          <h4>Variance Chart Series</h4>
          {VARIANCE_SERIES_OPTIONS.map((seriesOption) => (
            <StoneXRow align='center'>
              <div style={{ minWidth: '280px' }}>{getSeriesDisplayLabel(seriesOption)}</div>
              <StoneXTextField
                label="Color"
                placeholder="Default"
                value={varianceSeriesSettings[seriesOption]?.color ?? ''}
                onUpdate={(value) => updateVarianceSeriesColor(seriesOption, value)}
              />
              <StoneXTextField
                label="Opacity"
                placeholder="Default"
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={varianceSeriesSettings[seriesOption]?.opacity ?? ''}
                onUpdate={(value) => updateVarianceSeriesOpacity(seriesOption, value)}
              />
            </StoneXRow>
          ))}
        </div>
        </StoneXRow>
      )}
      {fixedCallCoverageChartEnabled && (
        <StoneXRow>
          <div>
            <h4>Fixed/Call Coverage Chart Series</h4>
            {FIXED_CALL_SERIES_OPTIONS.map((seriesOption) => (
              <StoneXRow align='center'>
                <div style={{ minWidth: '280px' }}>{getSeriesDisplayLabel(seriesOption)}</div>
                <StoneXTextField
                  label="Color"
                  placeholder="Default"
                  value={fixedCallSeriesSettings[seriesOption]?.color ?? ''}
                  onUpdate={(value) => updateFixedCallSeriesColor(seriesOption, value)}
                />

                <StoneXTextField
                  label="Opacity"
                  placeholder="Default"
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={fixedCallSeriesSettings[seriesOption]?.opacity ?? ''}
                  onUpdate={(value) => updateFixedCallSeriesOpacity(seriesOption, value)}
                />
              </StoneXRow>
            ))}
          </div>
        </StoneXRow>
      )}
      {volumeChartEnabled && (
        <StoneXRow>
          <div>
            <h4>Volume Chart Series</h4>
            {VOLUME_SERIES_OPTIONS.map((seriesOption) => (
              <StoneXRow align='center'>
                <div style={{ minWidth: '280px' }}>{getSeriesDisplayLabel(seriesOption)}</div>
                <StoneXCheckbox
                  label="Enabled"
                  checked={volumeSeriesSettings[seriesOption]?.enabled ?? true}
                  onUpdate={(value) => updateVolumeSeriesEnabled(seriesOption, value)}
                />
                <StoneXTextField
                  label="Color"
                  placeholder="Default"
                  value={volumeSeriesSettings[seriesOption]?.color ?? ''}
                  onUpdate={(value) => updateVolumeSeriesColor(seriesOption, value)}
                />
                <StoneXTextField
                  label="Opacity"
                  placeholder="Default"
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={volumeSeriesSettings[seriesOption]?.opacity ?? ''}
                  onUpdate={(value) => updateVolumeSeriesOpacity(seriesOption, value)}
                />
              </StoneXRow>
            ))}
          </div>
        </StoneXRow>
      )}

      <StoneXDivider />

      <StoneXRow>
        <h3>Columns</h3>
      </StoneXRow>
      <StoneXRow align='start'>
        <div style={{ flex: '1 1' }}>
          <p>Step 1: Choose which columns are available to the customer.</p>
          <StoneXTransferList
            leftList={unavailableColumns}
            leftHeader="Unavailable Columns"
            rightList={availableColumns}
            rightHeader="Available Columns"
            getId={(x) => VarianceColumns[x].colId}
            getGroup={(x) => VarianceColumns[x].parentHeader}
            getSortingOrder={(x) => columnOrderMap[x]}
            getOptionLabel={(x) => VarianceColumns[x].columnName ?? VarianceColumns[x].headerName ?? x}
            onUpdate={onAvailableColumnsUpdate}
          />
        </div>

        <div style={{ flex: '1 1' }}>
          <p>Step 2: From available columns, choose which ones are active by default.</p>
          <StoneXTransferList
            leftList={inactiveColumns}
            leftHeader="Available but Inactive"
            rightList={activeColumns}
            rightHeader="Active Columns"
            getId={(x) => VarianceColumns[x].colId}
            getGroup={(x) => VarianceColumns[x].parentHeader}
            getSortingOrder={(x) => columnOrderMap[x]}
            getOptionLabel={(x) => VarianceColumns[x].columnName ?? VarianceColumns[x].headerName ?? x}
            onUpdate={onActiveColumnsUpdate}
          />
        </div>
      </StoneXRow>

      <StoneXRow justify="end" marginDirection="top">
        <StoneXButton variant="outlined" onClick={() => onDeleteReport(report.customerReportId)}>Delete Report</StoneXButton>
        <StoneXButton variant="outlined" onClick={resetConfiguration}>Reset</StoneXButton>
        <StoneXButton isWorking={isSaving} onClick={saveConfiguration}>Save</StoneXButton>
      </StoneXRow>
    </div>
  );
}
